import { FileSearch, MapPin, Target } from "lucide-react";
import type { ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Seo } from "../components/layout/Seo";
import { Button } from "../components/ui/Button";
import { TextLink } from "../components/ui/TextLink";
import { Section } from "../components/ui/Section";
import { insights } from "../content/insights";
import { isLocale, localizedPath } from "../lib/routes";

const sec = "py-[clamp(36px,5vw,48px)]";
const scrollMt = "scroll-mt-28";

const principles = [
  [FileSearch, "Research-backed", "Sources, data and evidence before conclusions."],
  [Target, "Decision-oriented", "Research built around real choices and constraints."],
  [MapPin, "Field-informed", "Operator experience, expert input and practical context."]
];

const formats = [
  ["Research Briefs", "Deep analysis of a defined operational problem."],
  ["Market Intelligence", "Market-fit, localization and technology-transfer questions."],
  ["Sector Studies", "Cross-system analysis of a specific value chain or industry."],
  ["Field Notes", "Short observations from interviews, pilots and practical work."]
];

const agenda: Array<[string, string, string, string[]]> = [
  ["01", "Energy resilience", "How can energy-intensive Moldovan agri-food businesses reduce exposure to grid instability and energy-price volatility?", ["BESS", "Energy management", "Efficiency", "Financing"]],
  ["02", "Climate-smart agriculture", "Which Israeli technologies can create measurable value under Moldova’s agricultural and climatic conditions?", ["Agrivoltaics", "Water", "Irrigation", "Crop resilience"]],
  ["03", "Technology transfer", "What has to happen between a working technology in Israel and a bankable deployment in Moldova?", ["Market fit", "Localization", "Regulation", "Local partners"]],
  ["04", "Finance & implementation", "Which combinations of commercial finance, public programmes and project design can make transition projects viable?", ["Feasibility", "Green finance", "Project design", "Investment readiness"]]
];

const featured = {
  label: "RESEARCH BRIEF · IN PREPARATION",
  title: "Moldova Energy Resilience for Agri-Food",
  lead: "A practical study of energy constraints, storage opportunities and decision criteria for wineries, food processors and other energy-intensive agricultural businesses.",
  examine: [
    "Where energy costs and interruptions materially affect operations",
    "Where storage and energy management may have a credible business case",
    "What operational data is required before selecting technology",
    "Which regulatory and financing constraints shape deployment"
  ],
  metadata: "Research Brief · Moldova · Energy Systems",
  status: "In preparation",
  image: "/assets/insights/insights-energy-resilience-cover.webp",
  alt: "Cover of the forthcoming TransitionBridge research brief Moldova Energy Resilience for Agri-Food."
};

const upcoming = [
  {
    type: "MARKET-FIT NOTE",
    title: "Israeli Climate-Tech Opportunities in Moldova",
    description: "A sector-by-sector assessment of where Israeli climate technologies may have a credible route into Moldova, and where market conditions are not yet ready.",
    metadata: ["Market Intelligence", "Moldova / Israel", "Technology Transfer"],
    image: "/assets/insights/Israeli%20Climate-Tech%20Opportunities%20in%20Moldova.webp",
    alt: "Cover of the forthcoming TransitionBridge market-fit note Israeli Climate-Tech Opportunities in Moldova."
  },
  {
    type: "SECTOR STUDY",
    title: "Green Wine Moldova: Energy, Water & Climate",
    description: "A vineyard-to-winery view of sustainability, combining agricultural resilience, energy use, water management and implementation pathways.",
    metadata: ["Sector Study", "Moldova", "Wine & Agriculture"],
    image: "/assets/insights/insights-green-wine-cover.webp",
    alt: "Cover of the forthcoming TransitionBridge sector study Green Wine Moldova: Energy, Water and Climate."
  }
];

const method = [
  ["01", "Source", "Public data, regulation, research and sector evidence"],
  ["02", "Validate", "Interviews with operators, experts and technology teams"],
  ["03", "Compare", "Technical, economic and implementation alternatives"],
  ["04", "Stress-test", "Constraints, assumptions, risks and failure conditions"],
  ["05", "Translate", "Decision criteria, pilot questions and next actions"]
];

const themes = [
  ["Energy", "Storage, resilience, distributed energy and energy management", "border-brand-blue"],
  ["Agriculture", "Agrivoltaics, water, irrigation and climate resilience", "border-brand-green"],
  ["Markets", "Technology transfer, localization, regulation and market entry", "border-brand-cyan"],
  ["Finance", "Feasibility, project finance, public support and investment readiness", "border-brand-dark"]
];

function Eyebrow({ children }: { children: ReactNode }) {
  return <p className="eyebrow mb-4">{children}</p>;
}

function PublicationEntry({ item }: { item: (typeof upcoming)[number] }) {
  return (
    <article className="rounded-2xl border border-line bg-surface p-6 md:p-7">
      <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
        <div className="flex shrink-0 justify-center sm:w-[40%]">
          <img src={item.image} alt={item.alt} width={1122} height={1402} loading="lazy" decoding="async" className="max-h-[300px] w-full object-contain sm:max-h-none" />
        </div>
        <div className="flex-1">
          <p className="text-xs font-bold uppercase tracking-[0.14em] text-brand-blue">{item.type}</p>
          <h3 className="mt-3 text-2xl font-bold leading-tight">{item.title}</h3>
          <p className="mt-3 leading-7 text-ink-secondary">{item.description}</p>
          <p className="mt-5 text-sm text-ink-secondary">{item.metadata.join(" · ")}</p>
          <span className="mt-4 inline-flex items-center gap-2 rounded-full border border-line bg-bg-secondary px-3 py-1 text-xs font-bold uppercase tracking-[0.12em] text-ink-secondary">
            <span className="h-1.5 w-1.5 rounded-full bg-brand-cyan" aria-hidden="true" />In preparation
          </span>
        </div>
      </div>
    </article>
  );
}

export function Insights() {
  const { locale } = useParams();
  const activeLocale = isLocale(locale) ? locale : "en";

  return (
    <>
      <Seo title="Insights" description="Research and strategic intelligence from TransitionBridge on energy resilience, climate-smart agriculture, technology transfer, finance and sustainable transition between Moldova and Israel." />

      {/* SECTION 1 — HERO */}
      <section id="insights-hero" className="bg-bg-primary pt-[calc(var(--header-height)+40px)] pb-[clamp(36px,4vw,44px)]">
        <div className="container-grid">
          <div className="max-w-[900px]">
            <p className="eyebrow mb-5">INSIGHTS</p>
            <h1 className="text-balance text-[clamp(2.25rem,5.2vw,4.2rem)] font-extrabold leading-[1.03]">Evidence for decisions that have to work in the real world.</h1>
            <p className="mt-7 max-w-[760px] text-lg leading-8 text-ink-secondary md:text-xl">TransitionBridge studies the technical, economic, regulatory and institutional conditions behind sustainable transition between Moldova and Israel.</p>
            <p className="mt-4 max-w-[760px] text-lg leading-8 text-ink-secondary md:text-xl">Our research is designed to answer practical questions: what is ready, what is viable, where the constraints are, and what should be tested next.</p>
          </div>
          <div className="mt-11 grid gap-8 border-t border-line pt-8 sm:grid-cols-3">
            {principles.map(([Icon, title, body]) => {
              const I = Icon as typeof FileSearch;
              return (
                <div key={title as string} className="flex items-start gap-4">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-brand-blue/15 bg-brand-blue/[0.06] text-brand-blue"><I className="h-5 w-5" aria-hidden="true" /></span>
                  <div>
                    <h3 className="font-bold">{title as string}</h3>
                    <p className="mt-1 text-sm leading-6 text-ink-secondary">{body as string}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PUBLICATION FORMATS */}
      <section className="bg-bg-primary pt-[clamp(36px,4vw,44px)] pb-[clamp(36px,5vw,48px)]">
        <div className="container-grid">
          <p className="eyebrow mb-2">OUR OUTPUT</p>
          <h2 className="text-xl font-bold md:text-2xl">Research designed for implementation.</h2>
          <div className="mt-8 grid gap-x-10 gap-y-7 sm:grid-cols-2 lg:grid-cols-4">
            {formats.map(([title, body], i) => (
              <div key={title} className="border-t border-line pt-5">
                <p className="mb-2 text-xs font-bold text-brand-blue/60">{String(i + 1).padStart(2, "0")}</p>
                <h3 className="text-base font-bold">{title}</h3>
                <p className="mt-2 text-sm leading-6 text-ink-secondary">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 2 — RESEARCH AGENDA */}
      <section id="research-agenda" className={`${scrollMt} bg-bg-secondary ${sec}`}>
        <div className="container-grid">
          <div className="max-w-[760px]">
            <Eyebrow>RESEARCH AGENDA</Eyebrow>
            <h2 className="text-balance text-3xl font-bold leading-[1.06] md:text-[2.6rem]">What we are trying to understand</h2>
            <p className="mt-5 text-lg leading-8 text-ink-secondary">Our agenda focuses on questions that sit between technology, market conditions, regulation, finance and real-world implementation.</p>
          </div>

          <div className="mt-10 flex flex-col gap-10">
            <div className="order-2 md:order-1">
              <img src="/assets/insights/insights-research-agenda.webp" alt="TransitionBridge research agenda connecting energy resilience, climate-smart agriculture, technology transfer and finance with implementation-ready transition." width={1672} height={941} loading="lazy" decoding="async" className="w-full rounded-3xl border border-line object-contain" />
            </div>
            <ol className="order-1 grid gap-x-10 gap-y-8 md:order-2 md:grid-cols-2">
              {agenda.map(([num, title, q, topics]) => (
                <li key={num} className="border-t border-line pt-5">
                  <div className="flex items-baseline gap-3">
                    <span className="text-sm font-bold text-brand-blue/60">{num}</span>
                    <h3 className="text-lg font-bold">{title}</h3>
                  </div>
                  <p className="mt-3 leading-7 text-ink-secondary">{q}</p>
                  <ul className="mt-4 flex flex-wrap gap-2">
                    {(topics as string[]).map((t) => (
                      <li key={t} className="rounded-full border border-line bg-surface px-3 py-1 text-xs font-semibold text-ink-secondary">{t}</li>
                    ))}
                  </ul>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* SECTION 3 — FEATURED RESEARCH */}
      <section id="featured-research" className={`${scrollMt} bg-[#EEF2F5] ${sec}`}>
        <div className="container-grid">
          <div className="max-w-[760px]">
            <Eyebrow>FEATURED RESEARCH</Eyebrow>
            <h2 className="text-balance text-3xl font-bold leading-[1.06] md:text-[2.6rem]">Research now in preparation</h2>
          </div>
          <div className="mt-10 grid gap-10 lg:grid-cols-[0.9fr_1.4fr] lg:gap-14">
            <div className="order-2 flex justify-center lg:order-1">
              <img src={featured.image} alt={featured.alt} width={1122} height={1402} loading="lazy" decoding="async" className="max-h-[420px] w-full max-w-[380px] rounded-xl border border-line bg-white object-contain lg:max-h-none" />
            </div>
            <div className="order-1 lg:order-2">
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-brand-blue">{featured.label}</p>
              <h3 className="mt-3 text-3xl font-bold leading-tight md:text-[2.2rem]">{featured.title}</h3>
              <p className="mt-4 max-w-[680px] text-lg leading-8 text-ink-secondary">{featured.lead}</p>
              <p className="mt-7 text-xs font-bold uppercase tracking-[0.16em] text-ink-secondary">The study will examine</p>
              <ul className="mt-4 grid gap-3">
                {featured.examine.map((item) => (
                  <li key={item} className="flex items-start gap-3 leading-7 text-ink-secondary">
                    <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-cyan" aria-hidden="true" />{item}
                  </li>
                ))}
              </ul>
              <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-3 border-t border-line pt-5">
                <p className="text-sm text-ink-secondary">{featured.metadata}</p>
                <span className="inline-flex items-center gap-2 rounded-full border border-line bg-white px-3 py-1 text-xs font-bold uppercase tracking-[0.12em] text-ink-secondary">
                  <span className="h-1.5 w-1.5 rounded-full bg-brand-cyan" aria-hidden="true" />{featured.status}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 4 — UPCOMING INSIGHTS */}
      <section id="upcoming-insights" className={`${scrollMt} bg-bg-primary ${sec}`}>
        <div className="container-grid">
          <div className="max-w-[760px]">
            <Eyebrow>UPCOMING</Eyebrow>
            <h2 className="text-balance text-3xl font-bold leading-[1.06] md:text-[2.6rem]">Next in the research pipeline</h2>
            <p className="mt-5 text-lg leading-8 text-ink-secondary">Two additional studies are being scoped around technology transfer and sector-level transition.</p>
          </div>
          <div className="mt-10 grid gap-6 md:grid-cols-2">
            {upcoming.map((item) => <PublicationEntry key={item.title} item={item} />)}
          </div>
        </div>
      </section>

      {/* SECTION 5 — OUR METHOD */}
      <section id="research-method" className={`${scrollMt} bg-bg-secondary ${sec}`}>
        <div className="container-grid">
          <div className="max-w-[760px]">
            <Eyebrow>OUR METHOD</Eyebrow>
            <h2 className="text-balance text-3xl font-bold leading-[1.06] md:text-[2.6rem]">From information to decision-grade evidence</h2>
            <p className="mt-5 text-lg leading-8 text-ink-secondary">We combine public evidence with field knowledge, then test assumptions before translating findings into practical choices.</p>
          </div>

          {/* Desktop horizontal timeline */}
          <div className="relative mt-12 hidden lg:block">
            <div className="absolute left-0 right-0 top-4 h-px bg-[linear-gradient(90deg,rgba(36,87,245,0.45),rgba(18,175,203,0.45))]" aria-hidden="true" />
            <ol className="grid grid-cols-5 gap-6">
              {method.map(([num, title, body]) => (
                <li key={num} className="relative">
                  <span className="absolute left-0 top-0 grid h-8 w-8 place-items-center rounded-full border border-brand-cyan/50 bg-bg-secondary text-xs font-bold text-brand-cyanDark" aria-hidden="true">{num}</span>
                  <div className="pt-12">
                    <h3 className="text-[1.0625rem] font-semibold">{title}</h3>
                    <p className="mt-2 text-sm leading-6 text-ink-secondary">{body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          {/* Mobile vertical timeline */}
          <div className="relative mt-10 lg:hidden">
            <div className="absolute left-[15px] top-3 bottom-3 w-px bg-[linear-gradient(180deg,rgba(36,87,245,0.45),rgba(18,175,203,0.45))]" aria-hidden="true" />
            <ol className="grid gap-7">
              {method.map(([num, title, body]) => (
                <li key={num} className="relative ps-12">
                  <span className="absolute start-0 top-0 grid h-8 w-8 place-items-center rounded-full border border-brand-cyan/50 bg-bg-secondary text-xs font-bold text-brand-cyanDark" aria-hidden="true">{num}</span>
                  <h3 className="text-[1.0625rem] font-semibold">{title}</h3>
                  <p className="mt-1.5 text-sm leading-6 text-ink-secondary">{body}</p>
                </li>
              ))}
            </ol>
          </div>

          <p className="mt-12 border-l-4 border-brand-blue ps-6 text-xl font-bold text-ink-primary md:text-2xl">When evidence is incomplete, we say so.</p>
        </div>
      </section>

      {/* SECTION 6 — EXPLORE BY THEME */}
      <section id="themes" className={`${scrollMt} bg-bg-primary ${sec}`}>
        <div className="container-grid">
          <div className="max-w-[760px]">
            <Eyebrow>THEMES</Eyebrow>
            <h2 className="text-balance text-3xl font-bold leading-[1.06] md:text-[2.6rem]">Explore by theme</h2>
            <p className="mt-5 text-lg leading-8 text-ink-secondary">Our research library will grow around four recurring areas.</p>
          </div>
          <div className="mt-10 grid gap-10 sm:grid-cols-2 md:grid-cols-4 md:gap-8">
            {themes.map(([title, body, border]) => (
              <div key={title} className={`border-t-2 ${border} pt-5`}>
                <h3 className="text-lg font-bold">{title}</h3>
                <p className="mt-2 text-sm leading-6 text-ink-secondary">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 7 — RESEARCH CONTRIBUTION CTA */}
      <section className="petrol-section">
        <div className="container-grid pt-[clamp(40px,5vw,52px)] pb-[clamp(48px,6vw,64px)]">
          <div className="max-w-[820px]">
            <p className="eyebrow-sage mb-4">CONTRIBUTE</p>
            <h2 className="text-balance text-3xl font-bold leading-[1.06] text-brand-darkText md:text-[2.6rem]">Have data, experience or a question we should investigate?</h2>
            <p className="mt-5 max-w-[720px] text-lg leading-8 text-brand-darkMuted">We welcome input from Moldovan operators, Israeli technology teams, researchers and institutions working on the same problems.</p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <Button to={localizedPath(activeLocale, "contact")}>Contribute to our research</Button>
              <Button variant="darkGhost" to={localizedPath(activeLocale, "contact")}>Start a conversation</Button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export function InsightDetail() {
  const { slug, locale } = useParams();
  const { t } = useTranslation();
  const activeLocale = isLocale(locale) ? locale : "en";
  const insight = insights.find((item) => item.slug === slug && item.status !== "draft");

  if (!insight) {
    return <Section title="Insight not found" className="pt-[calc(var(--header-height)+72px)]" />;
  }

  return (
    <>
      <Seo title={insight.title[activeLocale]} description={insight.subtitle[activeLocale]} />
      <article className="pt-[calc(var(--header-height)+72px)]">
        <div className="container-grid grid gap-10 pb-16 lg:grid-cols-[240px_1fr]">
          <aside className="hidden lg:block">
            <div className="sticky top-28 border-s border-line ps-5 text-sm text-ink-secondary">
              <p className="font-bold text-ink-primary">Contents</p>
              <a className="mt-3 block hover:text-brand-blue" href="#article">Overview</a>
              <button className="mt-6 rounded-lg border border-line px-3 py-2 text-sm font-semibold">Share</button>
            </div>
          </aside>
          <div>
            <nav className="mb-8 text-sm text-ink-secondary"><Link to={localizedPath(activeLocale, "insights")}>{t("nav.insights")}</Link> / {insight.category}</nav>
            <span className="mb-6 inline-flex rounded-full border border-line bg-surface px-3 py-1 text-xs font-bold uppercase text-ink-secondary">{insight.status}</span>
            <h1 className="max-w-4xl text-4xl font-extrabold leading-tight md:text-6xl">{insight.title[activeLocale]}</h1>
            <p className="mt-6 max-w-3xl text-xl leading-8 text-ink-secondary">{insight.subtitle[activeLocale]}</p>
            <div className="mt-10 h-1 rounded-full bg-line"><div className="h-1 w-1/3 rounded-full bg-brand-blue" /></div>
            <div id="article" className="mt-12 max-w-3xl space-y-6 text-lg leading-8 text-ink-secondary">
              {insight.body[activeLocale].map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
            </div>
          </div>
        </div>
      </article>
    </>
  );
}
